/**
 * Smart symbol text substitutions, mirroring Slack's `textsubstitutions`
 * module. When the user finishes typing a pattern it is replaced with its
 * unicode form: `(c)` → ©, `-->` → →, `<=` → ≤.
 *
 * Slack gates this on the caret not being inside a code span/block; callers
 * enforce that. Emoticon auto-conversion is intentionally NOT included — it
 * surprises users.
 */

export type Substitution = { pattern: string; replacement: string };

/** ASCII substitutions applied while typing (longest match wins). */
export const SMART_SYMBOLS: Substitution[] = [
  { pattern: "(c)", replacement: "©" },
  { pattern: "(r)", replacement: "®" },
  { pattern: "(tm)", replacement: "™" },
  { pattern: "(p)", replacement: "§" },
  { pattern: "(e)", replacement: "€" },
  { pattern: "...", replacement: "…" },
  { pattern: "-->", replacement: "→" },
  { pattern: "<--", replacement: "←" },
  { pattern: "<-->", replacement: "↔" },
  { pattern: "<=", replacement: "≤" },
  { pattern: ">=", replacement: "≥" },
  { pattern: "!=", replacement: "≠" },
  { pattern: "1/2", replacement: "½" },
  { pattern: "1/4", replacement: "¼" },
  { pattern: "3/4", replacement: "¾" },
  { pattern: "+/-", replacement: "±" },
];

function longestAt(text: string): Substitution | null {
  let best: Substitution | null = null;
  for (const entry of SMART_SYMBOLS) {
    if (!text.endsWith(entry.pattern)) continue;
    if (!best || entry.pattern.length > best.pattern.length) best = entry;
  }
  return best;
}

/**
 * Returns the smart symbol whose pattern ends exactly at the caret, if any.
 * `text` is everything before (and including) the just-typed character, in
 * plain text with no block newlines. A preceding word character prevents the
 * swap so `program(c)` stays literal.
 */
export function matchSmartSymbol(text: string): Substitution | null {
  const match = longestAt(text);
  if (!match) return null;
  const before = text[text.length - match.pattern.length - 1];
  if (before !== undefined && /[\w]/.test(before)) return null;
  return match;
}
